import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { debounce, race, Subject, timer } from 'rxjs';

import { type DatasetColumn } from '@blaze-shared/schema-domain';

import { type ExtraStartAction } from '../../blaze-table/blaze-table.model';
import { BlazeTableLoggerService } from '../../shared/blaze-table-logger.service';
import { type BlazeTableValue } from '../../shared/blaze-table.service';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseCellInputComponent<T = unknown> implements OnInit, AfterViewInit, OnDestroy {
  private readonly logger = inject(BlazeTableLoggerService);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly injector = inject(Injector);

  @Input() column!: DatasetColumn;
  @Input() value: BlazeTableValue;
  @Input() isDisabled = false;
  @Input() forceFocusAfterInit = false;
  @Input() extraStartAction?: ExtraStartAction;
  @Input() rowNum: number;
  @Input() selectedText?: string;
  @Input() selectedTextOffset?: number;

  @Output() valueChanged = new EventEmitter<T | null>();
  @Output() focusLost = new EventEmitter<boolean>();

  private readonly emitValue$ = new Subject<T | null>();

  formControl!: UntypedFormControl;

  get nativeElement(): HTMLInputElement | HTMLTextAreaElement | null {
    return null;
  }

  constructor() {
    this.emitValue$
      .pipe(
        debounce(() => race(timer(300), this.focusLost)),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.logger.log('🪧 BaseCellInput output emit:', value);
        this.valueChanged.emit(value);
      });
  }

  ngOnInit(): void {
    this.formControl = this.createFormControl();
  }

  ngAfterViewInit(): void {
    if (this.forceFocusAfterInit) {
      this.logger.log('Focus main element');
      this.focusMainElement();
    }

    if (this.extraStartAction) {
      this.logger.log('Execute extra start action:', this.extraStartAction);
      this.executeExtraStartAction();
    }
    this.selectText();
  }

  ngOnDestroy(): void {
    this.focusLost.emit(true);
    this.logger.log('Cell input destroyed', this.column?.name, this.column?.columnType);
  }

  @HostListener('keydown.esc', ['$event'])
  handleEscKey($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.focusLost.emit(true);
  }

  protected createFormControl(): FormControl<T | null> {
    this.logger.log('🪧 BaseCellInput init value:', this.value);
    const formControl = new FormControl({
      value: this.value as T | null,
      disabled: this.isDisabled,
    });
    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.emitValueChanged(value as T | null);
    });
    return formControl;
  }

  protected emitValueChanged(value: T | null): void {
    this.emitValue$.next(value);
  }

  protected selectText(): void {
    if (!this.selectedText || !this.nativeElement || this.extraStartAction) {
      return;
    }
    this.logger.log('Select text in cell input:', this.selectedText);

    afterNextRender(
      () => {
        this.nativeElement?.focus();
        this.nativeElement?.setSelectionRange(
          this.selectedTextOffset || 0,
          (this.selectedTextOffset || 0) + (this.selectedText || '').length,
        );
      },
      { injector: this.injector },
    );
  }
  protected focusMainElement(): void {
    this.nativeElement?.focus();
  }
  protected executeExtraStartAction(): void {
    this.logger.log('Execute extra start action:', this.extraStartAction || 'none');
  }
}
